import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useBusinessPartners, useInvalidateBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { MotionDiv } from '@/components/element/MotionDiv';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { BusinessPartner, SalesOrder } from '@/hooks/api/models';
import { FilterMatchMode } from 'primereact/api';
import { BUSINESS_PARTNER_DISPLAY_TYPES } from '@/lib/constants';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useDestroyBusinessPartner } from '@/hooks/api/0462_destroy_business_partner';
import { useLoading } from '@/providers/LoadingProvider';
import { toastApiError, toastSuccess } from '@/lib/toast';

export const BusinessPartnersElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, me } = useCurrentWorkspace();
  const [page, setPage] = useState(1);
  const [selectedBusinessPartner, setSelectedBusinessPartner] = useState<BusinessPartner>();
  const { data: businessPartners } = useBusinessPartners({
    identifier: _w_!.identifier,
    page: page,
    per: 1000,
    include_invalidity: true,
  });
  const { mutate, isSuccess, isPending, error } = useDestroyBusinessPartner();
  const { invalidate } = useInvalidateBusinessPartners(_w_!.identifier);

  const deleteConfirmOk = () => {
    mutate({ identifier: _w_!.identifier, id: selectedBusinessPartner!.id });
  };

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('取引先を削除しました');
      invalidate();
    }
  }, [isSuccess]);

  const onPageLinkClick = (_page: number) => {
    setPage(_page);
  };

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const stateTemplate = (bp: BusinessPartner) =>
    bp.invalidity ? (
      <span className="badge badge-sm badge-warning">無効</span>
    ) : (
      <span className="badge badge-sm badge-success">有効</span>
    );

  const displayTypeTemplate = (bp: BusinessPartner) => BUSINESS_PARTNER_DISPLAY_TYPES[bp.display_type] || '';

  const descriptionTemplate = (bp: BusinessPartner) => <SimpleTextFormat content={bp.description || ''} />;

  const editButtonTemplate = (bp: BusinessPartner) => (
    <>
      <Link
        to={`/w/${_w_!.identifier}/setting/business_partners/${bp.id}/edit`}
        className="btn btn-icon-only btn-outline-success me-1"
      >
        <i className="fas fa-edit text-success text-sm"></i>
      </Link>

      <button
        className="btn btn-icon-only btn-outline-danger"
        onClick={() => setSelectedBusinessPartner(bp)}
        data-bs-toggle="modal"
        data-bs-target={`#deleteConfirm`}
      >
        <i className="fas fa-trash-alt text-danger text-sm"></i>
      </button>
    </>
  );

  if (businessPartners == null) return <></>;

  if (businessPartners.business_partners.length == 0)
    return (
      <div className="card-body pt-0">
        <p className="card-description text-sm">登録されている取引先がありません。</p>
      </div>
    );

  return (
    <>
      <MotionDiv className="card-body pt-0">
        <div className="card-header p-0 pb-2 d-flex justify-content-between">
          <div></div>
          <div>
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                className="form-control"
                placeholder="Search"
                type="text"
                value={filters.global.value}
                onChange={(e) => {
                  const value = e.target.value;
                  let _filters = { ...filters };
                  _filters.global.value = value;
                  setFilters(_filters);
                }}
              />
            </div>
          </div>
        </div>

        <ModalConfirm
          modalId={'deleteConfirm'}
          okClicked={deleteConfirmOk}
          message={'取引先を削除します。よろしいですか？'}
        />

        <div className="table-responsive">
          <DataTable
            value={businessPartners.business_partners}
            stripedRows
            scrollable
            scrollHeight="800px"
            tableClassName="table data-table align-items-center"
            removableSort
            filters={filters}
            globalFilterFields={['invalidity', 'name', 'display_name', 'corporate_number', 'description']}
            emptyMessage="No Data."
          >
            <Column
              header="ステータス"
              field="invalidity"
              body={stateTemplate}
              bodyClassName=""
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              headerStyle={{ backgroundColor: '#f9fafb' }}
              bodyStyle={{ backgroundColor: 'inherit', width: '20px' }}
              frozen
              sortable
            ></Column>
            <Column
              header="正式名称"
              field="name"
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              headerStyle={{ backgroundColor: '#f9fafb' }}
              bodyStyle={{ backgroundColor: 'inherit', minWidth: '100px' }}
              // style={{ minWidth: '200px' }}
              // bodyStyle={{ backgroundColor: 'inherit', whiteSpace: 'normal', wordWrap: 'break-word' }}
              frozen
              sortable
            ></Column>
            <Column
              header="表示名"
              field="display_name"
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              sortable
            ></Column>
            <Column
              header="法人番号"
              field="corporate_number"
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              sortable
            ></Column>
            <Column
              header="表示設定"
              field="display_type"
              body={displayTypeTemplate}
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              sortable
            ></Column>
            <Column
              header="メモ"
              field="description"
              body={descriptionTemplate}
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
            ></Column>
            <Column
              header=""
              field=""
              body={editButtonTemplate}
              bodyClassName="text-xxs px-2 font-weight-bold text-wrap"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              style={{ minWidth: '100px' }}
            ></Column>
          </DataTable>
        </div>
      </MotionDiv>
    </>
  );
};
